if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      onSellingPlanChange = undefined;
      currentVariant = null;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.currentVariant = this.getSelectedVariant(this);
        this.onSellingPlanChange = (event) => {
          const detail = event.detail || {};
          if (detail.source === 'lgd-subscription-buy-box' && !this.contains(event.target)) return;

          this.updateOneTimeLoopDiscount();
          this.updateMainPrice(detail.planType);
        };
        document.addEventListener('selling-plan:change', this.onSellingPlanChange);

        this.initQuantityHandlers();

        // Call updateSellingPlan on initial load
        const variant = this.getSelectedVariant(this);
        if (variant) {
          this.updateSellingPlan(variant);
        }
        // Check initial variant on page load
        const initialVariant = this.getSelectedVariant(this);
        if (initialVariant) {
          // Update discount badges on initial load
          this.updateDiscountBadges(initialVariant);

          if (initialVariant.name?.includes('One-Time')) {
            const oneTimePurchase = this.querySelector('.purchase-option');
            if (oneTimePurchase) {
              oneTimePurchase.classList.remove('hidden');
            }
          }
        }

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.onSellingPlanChange) document.removeEventListener('selling-plan:change', this.onSellingPlanChange);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateSellingPlan(variant) {
        const sellingPlanInput = document.getElementById('variant-selects-selling-plan');
        if (!sellingPlanInput) return;

        if (variant?.selling_plan_allocations?.[0]) {
          const sellingPlan = variant.selling_plan_allocations[0];
          if (sellingPlan.selling_plan_id) {
            sellingPlanInput.value = sellingPlan.selling_plan_id;
          } else if (sellingPlan.selling_plan?.id) {
            sellingPlanInput.value = sellingPlan.selling_plan.id;
          } else {
            sellingPlanInput.value = '';
          }
        } else {
          sellingPlanInput.value = '';
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // Update selling plan
          this.updateSellingPlan(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }
          const variantDescription = document.querySelector('variant-description');
          if (variantDescription) {
            variantDescription.update(variant);
          }

          const announcementBarProductContainer = document.querySelector('.announcement-bar__product-container');
          if (announcementBarProductContainer) {
            announcementBarProductContainer.querySelector('.announcement-bar__product-variant').innerHTML =
              variant.title;

            const image = announcementBarProductContainer.querySelector('.announcement-bar__product-image');
            if (image) {
              if (variant.featured_image) {
                image.querySelector('img').src = variant.featured_image.src;
                image.querySelector('img').srcset = variant.featured_image.src;
              }
            }
          }

          if (window.location.pathname.includes('livingood-daily-lifestyle')) {
            const productSubmitButtonTemplate = document.querySelector('.product-form__buttons');
            if (productSubmitButtonTemplate) {
              const button = productSubmitButtonTemplate.querySelector('button');
              const buttonText = button.querySelector('#product-submit-button-text');
              if (buttonText) {
                const buttonLabel = buttonText.querySelector('[data-atc-label]');
                if (buttonLabel) {
                  buttonLabel.textContent = `Buy ${variant.title} Now`;
                } else {
                  // Preserve compatibility with older button markup.
                  const arrowElement = buttonText.querySelector('.icon-wrap');
                  buttonText.innerHTML = '';
                  buttonText.textContent = `Buy ${variant.title} Now `;
                  if (arrowElement) {
                    buttonText.appendChild(arrowElement);
                  }
                }
              }
            }
          }

          this.currentVariant = variant;
          this.updateDiscountBadges(variant);
          this.updateOneTimeLoopDiscount();

          if (variant.name.includes('One-Time')) {
            // Update one-time purchase price
            const oneTimePurchase = this.querySelector('.purchase-option');
            if (oneTimePurchase) {
              oneTimePurchase.classList.remove('hidden');
              const oneTimePurchaseHtml = html.querySelector('.purchase-option');
              if (oneTimePurchaseHtml) {
                oneTimePurchase.innerHTML = oneTimePurchaseHtml.innerHTML;
              }
            }
          } else {
            const oneTimePurchase = this.querySelector('.purchase-option');
            if (oneTimePurchase) {
              oneTimePurchase.classList.add('hidden');
            }
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          this.updateMainPrice();
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      parsePrice(text) {
        const digits = text.replace(/[^0-9.,]/g, '');
        const lastComma = digits.lastIndexOf(',');
        const lastDot = digits.lastIndexOf('.');
        let normalized;
        if (lastComma !== -1 && lastDot !== -1) {
          normalized = lastComma > lastDot
            ? digits.replace(/\./g, '').replace(',', '.')
            : digits.replace(/,/g, '');
        } else if (lastComma !== -1) {
          normalized = digits.replace(',', '.');
        } else {
          normalized = digits;
        }
        return parseFloat(normalized);
      }

      formatPrice(amount, referenceText) {
        const usesCommaDecimal = /\d,\d{2}(?!\d)/.test(referenceText);
        const formatted = amount.toFixed(2);
        return usesCommaDecimal ? formatted.replace('.', ',') : formatted;
      }

      get variantMetafields() {
        const el = document.getElementById(`variant-meta-${this.dataset.productId}`);
        return el ? JSON.parse(el.textContent) : {};
      }

      updateOneTimeLoopDiscount() {
        if (!this.currentVariant) return;

        const meta = this.variantMetafields[this.currentVariant.id];
        const discount = meta?.custom_discount;

        setTimeout(() => {
          const oneTimeOption = document.querySelector('.loop-widget-purchase-option-onetime');
          if (!oneTimeOption) return;

          const badge = oneTimeOption.querySelector('.loop-widget-purchase-option-discount-badge');
          const priceEl = oneTimeOption.querySelector('.loop-widget-purchase-option-price');
          const compareEl = oneTimeOption.querySelector('.loop-widget-purchase-option-compare-at-price');
          if (!badge || !priceEl) return;

          if (discount) {
            if (!priceEl.dataset.originalPrice) {
              priceEl.dataset.originalPrice = priceEl.textContent.trim();
            }
            const originalText = priceEl.dataset.originalPrice;
            const originalAmount = this.parsePrice(originalText);
            const discountedAmount = this.formatPrice(originalAmount * (1 - discount / 100), originalText);
            const currencyPrefix = originalText.match(/^[^0-9]*/)[0];

            badge.textContent = `Save ${discount}%`;
            badge.classList.remove('loop-display-none');

            priceEl.textContent = `${currencyPrefix}${discountedAmount}`;

            if (compareEl) {
              compareEl.textContent = originalText;
              compareEl.classList.remove('loop-display-none');
            }
          } else {
            badge.textContent = '';
            badge.classList.add('loop-display-none');

            if (compareEl) {
              compareEl.textContent = '';
              compareEl.classList.add('loop-display-none');
            }
          }
        }, 150);
      }

      setDiscountBadge(priceDiv, text) {
        let discountBadge = priceDiv.querySelector('.price__badge-discount');

        if (!text) {
          if (discountBadge) {
            discountBadge.textContent = '';
            discountBadge.classList.add('hidden');
          }
          return;
        }

        if (!discountBadge) {
          const saleBadge = priceDiv.querySelector('.price__badge-sale');
          discountBadge = document.createElement('span');
          discountBadge.className = saleBadge
            ? saleBadge.className.replace('price__badge-sale', 'price__badge-discount').replace(/\bhidden\b/g, '').trim()
            : 'badge price__badge-discount';
          priceDiv.appendChild(discountBadge);
        }

        discountBadge.textContent = text;
        discountBadge.classList.remove('hidden');
      }

      sameMoney(current, next) {
        if ((current || '') === (next || '')) return true;
        const left = this.parsePrice(current || '');
        const right = this.parsePrice(next || '');
        return Number.isFinite(left) && Number.isFinite(right) && Math.abs(left - right) <= 0.02;
      }

      showMainSalePrice(priceDiv, salePriceEl, compareEl, saleText, compareText, badgeText) {
        const currentBadge = priceDiv.querySelector('.price__badge-discount');
        const currentBadgeText = currentBadge && !currentBadge.classList.contains('hidden') ? currentBadge.textContent.trim() : '';
        const unchanged =
          priceDiv.classList.contains('price--on-sale') &&
          this.sameMoney(salePriceEl.textContent.trim(), saleText) &&
          this.sameMoney(compareEl?.textContent.trim() || '', compareText || '') &&
          currentBadgeText === (badgeText || '');

        priceDiv.querySelector('.price__badge-sale')?.classList.add('hidden');
        if (unchanged) return;

        priceDiv.classList.add('price--on-sale');
        if (compareEl && compareText) compareEl.textContent = compareText;
        salePriceEl.textContent = saleText;
        this.setDiscountBadge(priceDiv, badgeText);
      }

      showMainRegularPrice(priceDiv, regularPriceEl, priceText) {
        const currentBadge = priceDiv.querySelector('.price__badge-discount');
        const badgeHidden = !currentBadge || currentBadge.classList.contains('hidden') || currentBadge.textContent.trim() === '';
        const unchanged = !priceDiv.classList.contains('price--on-sale') && this.sameMoney(regularPriceEl.textContent.trim(), priceText) && badgeHidden;

        if (unchanged) return;

        priceDiv.classList.remove('price--on-sale');
        regularPriceEl.textContent = priceText;
        this.setDiscountBadge(priceDiv, '');
      }

      getActivePurchasePrices(planType) {
        const buyBox = this.querySelector('lgd-subscription-purchase-options:not([hidden])');
        const customActive = this.hasAttribute('data-lgd-subscription-ui-active') && buyBox;

        if (customActive) {
          return {
            planType: buyBox.dataset.purchaseType === 'one-time' ? 'one-time' : 'subscription',
            subscriptionPriceText: buyBox.querySelector('[data-subscription-price]')?.textContent.trim() || '',
            retailPriceText: buyBox.querySelector('[data-subscription-retail-price]')?.textContent.trim() || '',
          };
        }

        const selectedLoopOption = this.querySelector('.loop-widget-purchase-option-selected');
        if (!selectedLoopOption) {
          return { planType: planType || '', subscriptionPriceText: '', retailPriceText: '' };
        }

        return {
          planType: selectedLoopOption.hasAttribute('data-loop-widget-onetime-purchase-option') ? 'one-time' : 'subscription',
          subscriptionPriceText: selectedLoopOption.querySelector('.loop-widget-purchase-option-price')?.textContent.trim() || '',
          retailPriceText: '',
        };
      }

      updateMainPrice(planType) {
        const apply = () => this.applyMainPrice(planType);
        if (this.hasAttribute('data-lgd-subscription-ui-active')) {
          apply();
          return;
        }
        window.setTimeout(apply, 150);
      }

      applyMainPrice(planType) {
          const priceWrapper = this.querySelector(`#price-${this.dataset.section}`);
          if (!priceWrapper) return;

          const priceDiv = priceWrapper.querySelector('.price');
          if (!priceDiv) return;

          const regularPriceEl = priceDiv.querySelector('.price__regular .price-item--regular');
          const salePriceEl = priceDiv.querySelector('.price__sale .price-item--sale');
          const compareEl = priceDiv.querySelector('.price__sale s.price-item--regular');
          if (!regularPriceEl || !salePriceEl) return;

          const purchase = this.getActivePurchasePrices(planType);
          if (!purchase.planType) return;

          priceDiv.querySelector('.price__badge-sale')?.classList.add('hidden');

          if (!priceDiv.dataset.mainOneTimePrice) {
            const oneTimePriceText = priceDiv.classList.contains('price--on-sale')
              ? salePriceEl.textContent.trim()
              : regularPriceEl.textContent.trim();

            priceDiv.dataset.mainOneTimePrice = oneTimePriceText;
            priceDiv.dataset.mainCompareAtPrice = compareEl?.textContent.trim() || '';
          }

          const oneTimePriceText = priceDiv.dataset.mainOneTimePrice;
          const compareAtPriceText = priceDiv.dataset.mainCompareAtPrice;
          const oneTimeAmount = this.parsePrice(oneTimePriceText);
          const compareAtAmount = this.parsePrice(compareAtPriceText);
          const hasValidCompareAt =
            compareEl &&
            Number.isFinite(compareAtAmount) &&
            Number.isFinite(oneTimeAmount) &&
            compareAtAmount > oneTimeAmount;

          if (purchase.planType === 'subscription') {
            const subscriptionAmount = this.parsePrice(purchase.subscriptionPriceText);
            if (!purchase.subscriptionPriceText || !Number.isFinite(subscriptionAmount)) return;

            let comparisonPriceText = hasValidCompareAt ? compareAtPriceText : oneTimePriceText;
            let comparisonAmount = hasValidCompareAt ? compareAtAmount : oneTimeAmount;
            const retailAmount = this.parsePrice(purchase.retailPriceText);

            if (Number.isFinite(retailAmount) && retailAmount > subscriptionAmount) {
              comparisonPriceText = purchase.retailPriceText;
              comparisonAmount = retailAmount;
            }

            if (Number.isFinite(comparisonAmount) && comparisonAmount > subscriptionAmount) {
              const discountPercentage = Math.round((1 - subscriptionAmount / comparisonAmount) * 100);
              this.showMainSalePrice(
                priceDiv,
                salePriceEl,
                compareEl,
                purchase.subscriptionPriceText,
                comparisonPriceText,
                discountPercentage > 0 ? `Save ${discountPercentage}%` : ''
              );
              return;
            }

            this.showMainRegularPrice(priceDiv, regularPriceEl, purchase.subscriptionPriceText);
            return;
          }

          const meta = this.currentVariant ? this.variantMetafields[this.currentVariant.id] : null;
          const customDiscount = Number(meta?.custom_discount) || 0;

          if (customDiscount > 0 && Number.isFinite(oneTimeAmount)) {
            const discountedAmount = oneTimeAmount * (1 - customDiscount / 100);
            const formattedAmount = this.formatPrice(discountedAmount, oneTimePriceText);
            const currencyPrefix = oneTimePriceText.match(/^[^0-9]*/)?.[0] || '';

            this.showMainSalePrice(
              priceDiv,
              salePriceEl,
              compareEl,
              `${currencyPrefix}${formattedAmount}`,
              oneTimePriceText,
              `Save ${customDiscount}%`
            );
            return;
          }

          if (hasValidCompareAt) {
            const discountPercentage = Math.round((1 - oneTimeAmount / compareAtAmount) * 100);
            this.showMainSalePrice(
              priceDiv,
              salePriceEl,
              compareEl,
              oneTimePriceText,
              compareAtPriceText,
              discountPercentage > 0 ? `${discountPercentage}% OFF` : ''
            );
            return;
          }

          this.showMainRegularPrice(priceDiv, regularPriceEl, oneTimePriceText);
      }

      updateDiscountBadges(variant) {
        if (!variant) return;
        const isSubscription = !variant.selling_plan_allocations;

        const badges = this.querySelectorAll('.discount-badge');

        const labels = document.querySelectorAll('.discount-badge-label');
        labels.forEach((label) => {
          label.style.paddingTop = '15px';
        });
        badges.forEach((badge) => {
          badge.classList.remove('hidden');

          if (isSubscription) {
            if (badge.dataset.subscriptionDiscount) {
              badge.textContent = badge.dataset.subscriptionDiscount;
            }
          } else {
            if (badge.dataset.discount) {
              badge.textContent = badge.dataset.discount;
            }
          }
        });
      }
    }
  );
}
